import Select from "react-select";

type PennDropdownOption = { value: string; label: string; isDisabled?: boolean };

type PennDropdownProps = {
  options: PennDropdownOption[];
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  error?: boolean;
  isSearchable?: boolean;
  isDisabled?: boolean;
  name?: string;
  inputId?: string;
  className?: string;
  /**
   * Accessible name for the combobox (WCAG 4.1.2). react-select renders an
   * internal <input> with no <label>, so without this axe flags a `label`
   * critical. Explicit `ariaLabel` wins; otherwise, when there is no `inputId`
   * bound to an external <label>, the human-readable `placeholder` is used so
   * every standalone dropdown still has a name. (When `inputId` is set the
   * external <label htmlFor> provides the name, so we don't override it.)
   */
  ariaLabel?: string;
  /**
   * "default" — full bordered control (forms).
   * "bare" — borderless, transparent, compact: used when the dropdown sits
   * inside another bordered container (e.g. the navbar "Region" pill) so we
   * don't get a box-in-box look.
   */
  variant?: "default" | "bare";
};

export const PennDropdown = ({
  options,
  value,
  onChange,
  onBlur,
  placeholder = "Select…",
  error = false,
  isSearchable = false,
  isDisabled = false,
  name,
  inputId,
  ariaLabel,
  className = "",
  variant = "default",
}: PennDropdownProps) => {
  const selected = options.find((opt) => opt.value === value) ?? null;
  const isBare = variant === "bare";
  // WCAG 4.1.2: guarantee an accessible name. Don't clobber an inputId-bound
  // external <label> (those dropdowns already have a name).
  const resolvedAriaLabel = ariaLabel ?? (inputId ? undefined : placeholder);

  return (
    <Select<PennDropdownOption>
      unstyled
      name={name}
      inputId={inputId}
      aria-label={resolvedAriaLabel}
      options={options}
      value={selected}
      onChange={(opt) => onChange?.(opt?.value ?? "")}
      onBlur={onBlur}
      placeholder={placeholder}
      isSearchable={isSearchable}
      isDisabled={isDisabled}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      className={className}
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      classNames={{
        control: (state) =>
          isBare
            ? [
                "flex items-center justify-between w-full h-8 text-body-medium bg-transparent",
                isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
              ].join(" ")
            : [
                "flex items-center justify-between w-full min-h-[42px] px-3 rounded-xl border text-sm transition",
                isDisabled
                  ? "bg-gray-100 opacity-50 cursor-not-allowed"
                  : "bg-white cursor-pointer",
                error
                  ? "border-error"
                  : state.isFocused
                    ? "border-penn-500 ring-2 ring-penn-500"
                    : "border-gray-200 hover:border-penn-500",
              ].join(" "),
        valueContainer: () => (isBare ? "flex items-center" : "flex flex-wrap items-center gap-1"),
        placeholder: () => "text-gray-400",
        // `bare` is only used in the navy nav bar, so its value text must be the
        // on-navy token, not penn-900 (navy on navy).
        singleValue: () => (isBare ? "text-on-navy text-body-medium" : "text-penn-text"),
        input: () => "text-penn-text",
        indicatorsContainer: () => "flex items-center",
        indicatorSeparator: () => "hidden",
        dropdownIndicator: () => (isBare ? "flex items-center px-1 text-on-navy-secondary" : "flex items-center px-1 text-gray-400"),
        menu: () =>
          "mt-1 rounded-xl border border-gray-200 bg-white shadow-soft overflow-hidden",
        menuList: () => "py-1 max-h-60 overflow-y-auto",
        option: (state) =>
          [
            "px-3 py-2 text-sm cursor-pointer transition-colors",
            state.isSelected
              ? "bg-penn-500 text-white"
              : state.isFocused
                ? "bg-penn-100 text-penn-text"
                : "text-penn-text",
          ].join(" "),
        noOptionsMessage: () => "px-3 py-2 text-sm text-gray-400",
      }}
    />
  );
};
